import { PaymentMethod } from "model/Gig";
import type { StaffProfileData } from "services/api/company/staff.service";
import { fromApiPhoneNumber } from "utils/phoneNumber.logic";
import type {
  EmploymentDetailsForm,
  PaymentMethodDetailsForm,
  PersonalDetailsForm,
} from "../profilePanel.contract";
import { toLocalDateOrNull } from "../domains/employment/profilePanel.payload.logic";
import { DEFAULT_VACATION_TERMS } from "../../shared/staffVacation.logic";
import type { AddStaffFormData } from "../../addStaffPanel/addStaffPanel.contract";

export const EMPTY_PERSONAL_DETAILS: PersonalDetailsForm = {
  firstName: "",
  lastName: "",
  phoneNumber: "",
  email: "",
  personalIdentityNumber: "",
  teamIds: [],
};

export const EMPTY_EMPLOYMENT_DETAILS: EmploymentDetailsForm = {
  employmentType: "",
  role: "",
  employeeNumber: "",
  startDate: null,
  endDate: null,
  salary: "",
  salaryType: "",
  taxTable: "",
  paymentType: "",
  employeeCategory: "",
  workPercentage: "",
  agreement: "",
  noticeDays: "",
  vacationDays: "",
  workplace: "",
  ...DEFAULT_VACATION_TERMS,
};

export const EMPTY_PAYMENT_METHOD_DETAILS: PaymentMethodDetailsForm = {
  type: "BankAccount",
  value: "",
};

const toVacationPayoutTypeOrDefault = (
  value: string | null | undefined,
  fallback: string,
): string => {
  if (value === "Automatic" || value === "Manual") return value;
  return fallback;
};

const toDaysOrDefault = (
  value: number | null | undefined,
  fallback: string,
): string => {
  return value == null ? fallback : String(value);
};

const toLatestEmploymentNumber = (profile: StaffProfileData | null): string => {
  const selected = profile?.employmentInfo?.selected?.employeeNumber;
  if (selected != null) return String(selected);

  for (const employment of profile?.employments ?? []) {
    if (employment.employeeNumber != null) {
      return String(employment.employeeNumber);
    }
  }

  return "";
};

export const toPersonalDetailsForm = (
  profile: StaffProfileData | null,
): PersonalDetailsForm => {
  return {
    firstName: profile?.firstName ?? "",
    lastName: profile?.lastName ?? "",
    phoneNumber: fromApiPhoneNumber(profile?.phoneNumber),
    email: profile?.email ?? "",
    personalIdentityNumber: profile?.personalIdentityNumber ?? "",
    teamIds: profile?.workGroups.map((workGroup) => String(workGroup.id)) ?? [],
  };
};

export const toEmploymentDetailsForm = (
  profile: StaffProfileData | null,
): EmploymentDetailsForm => {
  const selectedEmployment = profile?.employmentInfo?.selected;
  const activeSalary =
    selectedEmployment?.salaries?.find((salary) => salary.isActive) ?? null;
  const selectedSalaryValue = activeSalary?.value ?? selectedEmployment?.salary;

  return {
    employmentType: selectedEmployment?.employmentType ?? "",
    role: selectedEmployment?.role ?? "",
    employeeNumber:
      selectedEmployment?.employeeNumber == null
        ? ""
        : String(selectedEmployment.employeeNumber),
    startDate: toLocalDateOrNull(selectedEmployment?.startDate),
    endDate: toLocalDateOrNull(selectedEmployment?.endDate),
    salary: selectedSalaryValue == null ? "" : String(selectedSalaryValue),
    salaryType: selectedEmployment?.salaryType ?? "",
    taxTable:
      selectedEmployment?.taxTable == null
        ? ""
        : String(selectedEmployment.taxTable),
    paymentType: selectedEmployment?.paymentType || PaymentMethod.NONE,
    employeeCategory: selectedEmployment?.employeeCategory ?? "",
    workPercentage:
      profile?.workPercentage == null
        ? ""
        : String(Math.round(profile.workPercentage * 100)),
    agreement: selectedEmployment?.agreement ?? "",
    noticeDays:
      selectedEmployment?.noticeDays == null
        ? ""
        : String(selectedEmployment.noticeDays),
    vacationDays:
      selectedEmployment?.vacationDays == null
        ? ""
        : String(selectedEmployment.vacationDays),
    workplace: selectedEmployment?.workplace ?? "",
    vacationPayoutType: toVacationPayoutTypeOrDefault(
      selectedEmployment?.vacationPayoutType,
      DEFAULT_VACATION_TERMS.vacationPayoutType,
    ),
    maxSickLeaveDays: toDaysOrDefault(
      selectedEmployment?.maxSickLeaveDays,
      DEFAULT_VACATION_TERMS.maxSickLeaveDays,
    ),
    maxCareOfChildDays: toDaysOrDefault(
      selectedEmployment?.maxCareOfChildDays,
      DEFAULT_VACATION_TERMS.maxCareOfChildDays,
    ),
    maxParentalLeaveDays: toDaysOrDefault(
      selectedEmployment?.maxParentalLeaveDays,
      DEFAULT_VACATION_TERMS.maxParentalLeaveDays,
    ),
    // advanceVacationDays is absent from the GET response - the API only accepts
    // it when writing an employment - so there is nothing to carry over and a new
    // employment always starts its own grant from zero.
    advanceVacationDays: DEFAULT_VACATION_TERMS.advanceVacationDays,
  };
};

export const toPaymentMethodDetailsForm = (
  profile: StaffProfileData | null,
): PaymentMethodDetailsForm => {
  const paymentMethod = profile?.paymentMethods[0] ?? null;

  return {
    type: paymentMethod?.type ?? "BankAccount",
    value: paymentMethod?.value ?? "",
  };
};

/**
 * The values a new employment starts from. It starts the day after the
 * selected employment ends, has no end date of its own and keeps the selected
 * employment's terms and the worker's latest employee number. A term the
 * selected employment lacks is left out so the form's own default applies.
 * The personal identity number is passed along so employment templates can
 * filter their salaries by the worker's age.
 */
export const toNewEmploymentFormData = (
  profile: StaffProfileData | null,
): Partial<AddStaffFormData> => {
  const selectedEmployment = profile?.employmentInfo?.selected ?? null;
  const selectedEndDate = toLocalDateOrNull(selectedEmployment?.endDate);
  const selectedSalary =
    selectedEmployment?.salaries?.find((salary) => salary.isActive)?.value ??
    selectedEmployment?.salary;
  const vacationPayoutType = selectedEmployment?.vacationPayoutType;

  return {
    personalIdentityNumber: profile?.personalIdentityNumber ?? undefined,
    employeeNumber: toLatestEmploymentNumber(profile),
    startDate: selectedEndDate?.plusDays(1),
    employmentType: selectedEmployment?.employmentType ?? undefined,
    role: selectedEmployment?.role ?? undefined,
    employeeCategory: selectedEmployment?.employeeCategory ?? undefined,
    salaryType: selectedEmployment?.salaryType ?? undefined,
    salaryAmount: selectedSalary?.toString(),
    workPercentage:
      selectedEmployment?.workPercentage == null
        ? undefined
        : String(Math.round(selectedEmployment.workPercentage * 100)),
    paymentType: selectedEmployment?.paymentType ?? undefined,
    agreement: selectedEmployment?.agreement ?? undefined,
    noticeDays: selectedEmployment?.noticeDays?.toString(),
    vacationDays: selectedEmployment?.vacationDays?.toString(),
    workplace: selectedEmployment?.workplace ?? undefined,
    vacationPayoutType:
      vacationPayoutType === "Automatic" || vacationPayoutType === "Manual"
        ? vacationPayoutType
        : undefined,
    maxSickLeaveDays: selectedEmployment?.maxSickLeaveDays?.toString(),
    maxCareOfChildDays: selectedEmployment?.maxCareOfChildDays?.toString(),
    maxParentalLeaveDays: selectedEmployment?.maxParentalLeaveDays?.toString(),
  };
};
